import React, { useState } from 'react';
import { X, Trash2, CheckCircle, FileText, Calendar, User, DollarSign } from 'lucide-react';

export default function PrestacionesHistory({
  prestaciones,
  employees,
  onClose,
  onDelete,
  onMarkAsPaid
}) {
  const [filter, setFilter] = useState('all'); // all, calculado, pagado
  const [selectedPrestacion, setSelectedPrestacion] = useState(null);

  // Filtrar prestaciones de forma segura
  const filteredPrestaciones = prestaciones?.filter(p => {
    if (!p) return false; // Filtrar elementos null/undefined
    if (filter === 'all') return true;
    return p.estado === filter;
  }) || [];

  const getStatusBadge = (estado) => {
    const badges = {
      pendiente: 'bg-yellow-100 text-yellow-700',
      aprobado: 'bg-blue-100 text-blue-700',
      pagado: 'bg-green-100 text-green-700',
      anulado: 'bg-red-100 text-red-700'
    };
    return badges[estado] || badges.pendiente;
  };

  const getStatusText = (estado) => {
    const texts = {
      pendiente: 'Pendiente',
      aprobado: 'Aprobado',
      pagado: 'Pagado',
      anulado: 'Anulado'
    };
    return texts[estado] || estado;
  };

  // Helper para formatear montos de forma segura
  const formatMonto = (value) => {
    const num = parseFloat(value) || 0;
    return num.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // 🔥 FIX: Helper para obtener el monto de un concepto
  const getMonto = (prestacion, concepto) => {
    try {
      const value = prestacion[concepto];

      // Si es un número directamente, devolverlo
      if (typeof value === 'number') {
        return value;
      }

      // Si es un objeto con monto, extraerlo
      if (typeof value === 'object' && value?.monto !== undefined) {
        return parseFloat(value.monto) || 0;
      }

      // Si es string numérico, parsearlo
      if (typeof value === 'string') {
        return parseFloat(value) || 0;
      }

      return 0;
    } catch {
      return 0;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl max-w-6xl w-full my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Historial de Prestaciones</h2>
            <p className="text-sm text-gray-500 mt-1">
              {prestaciones?.length || 0} prestaciones registradas
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Filters */}
        <div className="p-6 border-b">
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'text-sm font-medium text-gray-500 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              Todas ({prestaciones?.length || 0})
            </button>
            <button
              onClick={() => setFilter('pendiente')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === 'pendiente'
                  ? 'bg-blue-600 text-white'
                  : 'text-sm font-medium text-gray-500 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              Pendientes ({prestaciones?.filter(p => p && p.estado === 'pendiente').length || 0})
            </button>
            <button
              onClick={() => setFilter('pagado')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === 'pagado'
                  ? 'bg-blue-600 text-white'
                  : 'text-sm font-medium text-gray-500 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              Pagadas ({prestaciones?.filter(p => p && p.estado === 'pagado').length || 0})
            </button>
          </div>
        </div>

        {/* List */}
        <div className="p-6 max-h-[600px] overflow-y-auto">
          {filteredPrestaciones.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                No hay prestaciones {filter !== 'all' && filter}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredPrestaciones
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .map(prestacion => {
                  // Obtener montos de forma segura
                  const vacacionesMonto = getMonto(prestacion, 'vacaciones');
                  const navidadMonto = getMonto(prestacion, 'salarioNavidad');
                  const preavisoMonto = getMonto(prestacion, 'preaviso');
                  const cesantiaMonto = getMonto(prestacion, 'cesantia');
                  const totalPrestaciones = parseFloat(prestacion.totalPrestaciones) || 0;

                  return (
                    <div
                      key={prestacion.id}
                      className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        {/* Info */}
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <User className="w-4 h-4 text-gray-400" />
                            <h3 className="font-semibold text-gray-900">
                              {prestacion.empleadoNombre || 'N/A'}
                            </h3>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(prestacion.estado)}`}>
                              {getStatusText(prestacion.estado)}
                            </span>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                            <div>
                              <p className="text-gray-500 text-xs">Departamento</p>
                              <p className="font-medium text-gray-700">
                                {prestacion.empleadoDepartamento || 'N/A'}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-500 text-xs">Cargo</p>
                              <p className="font-medium text-gray-700">
                                {prestacion.empleadoCargo || 'N/A'}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-500 text-xs">Antigüedad</p>
                              <p className="font-medium text-gray-700">
                                {prestacion.anosServicio || 0} años, {prestacion.mesesServicio || 0} meses, {prestacion.diasServicio || 0} días
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-500 text-xs">Fecha Cálculo</p>
                              <p className="font-medium text-gray-700">
                                {prestacion.fechaCalculo
                                  ? new Date(String(prestacion.fechaCalculo).split('T')[0] + 'T12:00:00').toLocaleDateString('es-DO')
                                  : 'N/A'
                                }
                              </p>
                            </div>
                          </div>

                          {/* Detalles del monto */}
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <div className="flex items-center gap-2 mb-2">
                              <DollarSign className="w-4 h-4 text-green-600" />
                              <span className="text-lg font-bold text-green-700">
                                RD$ {formatMonto(totalPrestaciones)}
                              </span>
                            </div>

                            {/* Conceptos de prestaciones */}
                            <div className="grid grid-cols-4 gap-2 text-xs mb-2">
                              <div>
                                <p className="text-gray-500">Vacaciones</p>
                                <p className="font-medium">
                                  RD$ {formatMonto(vacacionesMonto)}
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-500">Navidad</p>
                                <p className="font-medium">
                                  RD$ {formatMonto(navidadMonto)}
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-500">Preaviso</p>
                                <p className="font-medium">
                                  RD$ {formatMonto(preavisoMonto)}
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-500">Cesantía</p>
                                <p className="font-medium">
                                  RD$ {formatMonto(cesantiaMonto)}
                                </p>
                              </div>
                            </div>

                            {/* Conceptos adicionales si existen */}
                            {((prestacion.horasExtrasPendientes && prestacion.horasExtrasPendientes > 0) ||
                              (prestacion.comisionesPendientes && prestacion.comisionesPendientes > 0)) && (
                              <div className="mt-2 pt-2 border-t border-gray-100">
                                <p className="text-xs text-gray-500 mb-1">Conceptos Adicionales:</p>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  {prestacion.horasExtrasPendientes > 0 && (
                                    <div>
                                      <p className="text-orange-600">Horas Extras</p>
                                      <p className="font-medium">
                                        RD$ {formatMonto(prestacion.horasExtrasPendientes)}
                                      </p>
                                    </div>
                                  )}
                                  {prestacion.comisionesPendientes > 0 && (
                                    <div>
                                      <p className="text-indigo-600">Comisiones</p>
                                      <p className="font-medium">
                                        RD$ {formatMonto(prestacion.comisionesPendientes)}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 ml-4">
                          {(prestacion.estado === 'pendiente' || prestacion.estado === 'aprobado') && (
                            <button
                              onClick={() => {
                                if (confirm('¿Marcar esta prestación como pagada?')) {
                                  onMarkAsPaid(prestacion.id);
                                }
                              }}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Marcar como pagado"
                            >
                              <CheckCircle className="w-5 h-5" />
                            </button>
                          )}
                          <button
                            onClick={() => {
                              if (confirm('¿Eliminar esta prestación del historial?')) {
                                onDelete(prestacion.id);
                              }
                            }}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>

        {/* Footer con estadísticas */}
        {prestaciones && prestaciones.length > 0 && (
          <div className="p-6 border-t bg-gray-50">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-xs text-gray-500 mb-1">Total Calculado</p>
                <p className="text-lg font-bold text-gray-900">
                  RD$ {formatMonto(
                    prestaciones.filter(p => p).reduce((sum, p) => sum + (parseFloat(p.totalPrestaciones) || 0), 0)
                  )}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Pendiente de Pago</p>
                <p className="text-lg font-bold text-blue-600">
                  RD$ {formatMonto(
                    prestaciones
                      .filter(p => p && p.estado === 'pendiente')
                      .reduce((sum, p) => sum + (parseFloat(p.totalPrestaciones) || 0), 0)
                  )}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Pagado</p>
                <p className="text-lg font-bold text-green-600">
                  RD$ {formatMonto(
                    prestaciones
                      .filter(p => p && p.estado === 'pagado')
                      .reduce((sum, p) => sum + (parseFloat(p.totalPrestaciones) || 0), 0)
                  )}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
